import React from 'react';
import { Download, RefreshCw, Shield } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { openapiApi, INTEGRATED_BASE_URL } from '@/config/api';
import { useAuth } from '@/context/AuthContext';
import { getSecretsForExecute, subscribePlaygroundAuth } from './playgroundAuthStore';
import { filterReferenceSpec } from './apiReferenceSpec.mjs';
import 'swagger-ui-dist/swagger-ui.css';

function SummaryCard({ title, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{title}</div>
      <div className="mt-2 text-2xl font-bold text-slate-900">{value ?? '-'}</div>
    </div>
  );
}

export default function PrimeSwaggerExplorer({ docsVersion: docsVersionProp } = {}) {
  const { integratedToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionRef = React.useRef(integratedToken);
  sessionRef.current = integratedToken;

  const containerRef = React.useRef(null);
  const uiRef = React.useRef(null);
  const requestRef = React.useRef(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [coverage, setCoverage] = React.useState(null);
  const [uiConfig, setUiConfig] = React.useState(null);
  const [versions, setVersions] = React.useState([]);
  const [pointers, setPointers] = React.useState(null);
  const [releaseMeta, setReleaseMeta] = React.useState(null);
  const [classificationFilter, setClassificationFilter] = React.useState('all');

  const selectedVersion = docsVersionProp
    || searchParams.get('docsVersion')
    || 'live';

  const setSelectedVersion = React.useCallback((next) => {
    const params = new URLSearchParams(searchParams);
    if (!next || next === 'live') params.delete('docsVersion');
    else params.set('docsVersion', next);
    if (!params.get('tab')) params.set('tab', 'openapi');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const downloadSpec = React.useCallback(async (format) => {
    const path = selectedVersion && selectedVersion !== 'live'
      ? `/openapi/versions/${encodeURIComponent(selectedVersion)}?format=${format === 'yaml' ? 'yaml' : 'json'}`
      : (format === 'yaml' ? '/openapi.yaml' : '/openapi.json');
    const res = await openapiApi.get(path, { params: { scope: 'prime', classification: classificationFilter }, responseType: format === 'yaml' ? 'text' : 'json' });
    const blob = new Blob(
      [format === 'yaml' ? (typeof res.data === 'string' ? res.data : String(res.data)) : JSON.stringify(res.data, null, 2)],
      { type: format === 'yaml' ? 'application/yaml' : 'application/json' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = format === 'yaml'
      ? `prime-openapi-${selectedVersion || 'live'}.yaml`
      : `prime-openapi-${selectedVersion || 'live'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [selectedVersion, classificationFilter]);

  const mountSwagger = React.useCallback(async () => {
    const requestId = ++requestRef.current;
    setCoverage(null);
    setLoading(true);
    setError('');
    try {
      const specPath = selectedVersion && selectedVersion !== 'live'
        ? `/openapi/versions/${encodeURIComponent(selectedVersion)}`
        : '/openapi.json';

      const [specRes, configRes, releasesRes] = await Promise.all([
        openapiApi.get(specPath, { params: { scope: 'prime', classification: classificationFilter } }),
        openapiApi.get('/openapi/ui-config'),
        openapiApi.get('/openapi/releases').catch(() => ({ data: { data: null } })),
      ]);
      if (requestRef.current !== requestId) return;
      setUiConfig(configRes.data?.data || null);

      const releaseData = releasesRes.data?.data;
      setPointers(releaseData?.pointers || null);
      const publishedOrApproved = (releaseData?.releases || []).filter((r) =>
        ['Published', 'Approved', 'Superseded', 'Withdrawn'].includes(r.status)
      );
      setVersions(publishedOrApproved);

      if (selectedVersion && selectedVersion !== 'live') {
        try {
          const detail = await openapiApi.get(`/openapi/releases/${encodeURIComponent(selectedVersion)}`);
          if (requestRef.current !== requestId) return;
          setReleaseMeta(detail.data?.data?.manifest || null);
        } catch {
          if (requestRef.current === requestId) setReleaseMeta(null);
        }
      } else {
        setReleaseMeta(null);
      }

      const mod = await import('swagger-ui-dist/swagger-ui-bundle.js');
      if (requestRef.current !== requestId || !containerRef.current) return;
      const SwaggerUIBundle = mod.SwaggerUIBundle || mod.default?.SwaggerUIBundle || mod.default;
      if (!SwaggerUIBundle) throw new Error('SwaggerUIBundle failed to load');

      const isProd = String(configRes.data?.data?.tryItOutMutationsAllowed) === 'false';

      const filteredSpec = filterReferenceSpec(specRes.data, classificationFilter);
      setCoverage(filteredSpec['x-prime-reference']);
      if (uiRef.current) {
        uiRef.current.specActions.updateJsonSpec(filteredSpec);
        return;
      }
      uiRef.current = SwaggerUIBundle({
        spec: filteredSpec,
        domNode: containerRef.current,
        deepLinking: true,
        docExpansion: 'none',
        filter: true,
        displayRequestDuration: true,
        tryItOutEnabled: true,
        persistAuthorization: false,
        supportedSubmitMethods: configRes.data?.data?.supportedSubmitMethods
          || (isProd ? ['get', 'head', 'options'] : ['get', 'put', 'post', 'delete', 'options', 'head', 'patch', 'trace']),
        presets: [SwaggerUIBundle.presets.apis],
        layout: 'BaseLayout',
        requestInterceptor: (req) => {
          const secrets = getSecretsForExecute();
          const hasAuthHeader = Boolean(String(req.headers?.Authorization || req.headers?.authorization || '').trim());
          const hasApiKey = Boolean(String(req.headers?.['x-api-key'] || req.headers?.['X-Api-Key'] || '').trim());

          if (!hasAuthHeader && !hasApiKey) {
            req.headers = req.headers || {};
            if (secrets.authMode === 'apiKey' && secrets.apiKey) {
              req.headers['x-api-key'] = secrets.apiKey;
            } else if (secrets.authMode === 'bearer' && secrets.bearerToken) {
              req.headers.Authorization = `Bearer ${secrets.bearerToken.replace(/^Bearer\s+/i, '')}`;
            } else if (sessionRef.current) {
              req.headers.Authorization = `Bearer ${sessionRef.current}`;
            }
          }

          if (typeof req.url === 'string') {
            try {
              const abs = new URL(req.url, window.location.origin);
              const isPrimeApiHost = /integrated\.injourney\.id$/i.test(abs.hostname)
                || abs.hostname === 'localhost'
                || abs.hostname === '127.0.0.1';
              if (isPrimeApiHost && abs.origin !== window.location.origin) {
                req.url = `${window.location.origin}${INTEGRATED_BASE_URL}${abs.pathname}${abs.search}`;
              } else if (req.url.startsWith('/') && !req.url.startsWith(INTEGRATED_BASE_URL)) {
                req.url = `${INTEGRATED_BASE_URL}${req.url}`;
              }
            } catch {
              /* keep original */
            }
          }
          return req;
        },
      });
    } catch (e) {
      if (requestRef.current !== requestId) return;
      setError(e.response?.data?.message || e.message || 'Gagal memuat OpenAPI / Swagger UI.');
    } finally {
      if (requestRef.current === requestId) setLoading(false);
    }
  }, [selectedVersion, classificationFilter]);

  React.useEffect(() => {
    mountSwagger();
    const unsub = subscribePlaygroundAuth(() => { /* auth state available to interceptor via store */ });
    return () => {
      unsub();
      requestRef.current++;
    };
  }, [mountSwagger]);

  React.useEffect(() => () => { uiRef.current = null; }, []);

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-900">API Reference</h2>
            <p className="mt-1 text-sm text-slate-600">
              OpenAPI PRIME saja, dikelompokkan per module. Filter dan download mengikuti tampilan yang dipilih; snapshot release asli tetap tersimpan di Release Management.
            </p>
            {uiConfig && !uiConfig.tryItOutMutationsAllowed && (
              <p className="mt-2 flex items-center gap-2 text-xs font-semibold text-amber-700">
                <Shield className="h-3.5 w-3.5" />
                Production mode: Try it out terbatas pada method aman (GET/HEAD/OPTIONS).
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <select aria-label="API grouping and exposure filter" className="h-9 rounded-md border px-2 text-sm" value={classificationFilter} onChange={e => setClassificationFilter(e.target.value)}>
              <option value="all">All PRIME APIs</option><option value="business">PRIME-Owned / Business Groups</option><option value="EXTERNAL_MEMBER">External Member (Guide)</option><option value="eligible">Gateway POC Eligible (Draft)</option><option value="restricted">Restricted / System</option>
            </select>
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
              Docs version
              <select
                className="h-9 min-w-[140px] rounded-md border border-slate-200 px-2 text-sm font-semibold text-slate-800"
                value={selectedVersion || 'live'}
                onChange={(e) => setSelectedVersion(e.target.value)}
              >
                <option value="live">Live (current build)</option>
                {pointers?.activeDocumentationVersion && (
                  <option value={pointers.activeDocumentationVersion}>
                    Active published ({pointers.activeDocumentationVersion})
                  </option>
                )}
                {versions.map((r) => (
                  <option key={r.contractVersion} value={r.contractVersion}>
                    {r.contractVersion} — {r.status}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" onClick={() => mountSwagger()} className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button type="button" onClick={() => downloadSpec('json')} className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <Download className="h-4 w-4" /> OpenAPI JSON
            </button>
            <button type="button" onClick={() => downloadSpec('yaml')} className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <Download className="h-4 w-4" /> OpenAPI YAML
            </button>
          </div>
        </div>
        {releaseMeta && (
          <div className="mt-3 rounded border border-slate-100 bg-slate-50 p-3 text-xs text-slate-600">
            <span className="font-semibold text-slate-800">{releaseMeta.releaseTitle}</span>
            {' · '}
            Status: {releaseMeta.status}
            {' · '}
            Breaking: {releaseMeta.breakingChangeStatus || 'None'}
            {releaseMeta.artifactSha256 && (
              <> · SHA-256: <code className="break-all">{releaseMeta.artifactSha256.slice(0, 16)}…</code></>
            )}
          </div>
        )}
      </div>

      {coverage && selectedVersion === 'live' && (
        <div className="grid gap-3 md:grid-cols-4">
          <SummaryCard title="PRIME Scope Operations" value={coverage.primeOperations} />
          <SummaryCard title="Displayed Operations" value={coverage.visibleOperations} />
          <SummaryCard title="Displayed Module Groups" value={coverage.visibleGroups} />
          <SummaryCard title="Outside PRIME Excluded" value={coverage.outsidePrimeExcluded} />
        </div>
      )}

      {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {loading && <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">Memuat Swagger UI…</div>}
      {!loading && !error && coverage?.visibleOperations === 0 && <div role="status" className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-600">{classificationFilter === 'EXTERNAL_MEMBER' ? 'Tidak ada native PRIME API pada kategori ini. Dokumentasi provider tersedia di Member Integration Guide.' : 'Tidak ada operation PRIME yang sesuai dengan filter ini.'}</div>}

      <div className={`overflow-hidden rounded-lg border border-slate-200 bg-white ${loading || error || coverage?.visibleOperations === 0 ? 'hidden' : ''}`}>
        <div ref={containerRef} className="prime-swagger-ui min-h-[480px]" />
      </div>
    </div>
  );
}
